import { Button } from "@cmp-src/primitives/button";
import { cn, useDisclosure } from "@gettactic/helpers";
import { getIconColorForBackground } from "@gettactic/helpers/src/colors/iconColor";
import React from "react";
import { IconPicker } from "../../controls";
import { IconFromSet, IconValue } from "../../icons";
import {
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../primitives";
import { FormControlWrapper } from "../FormControlWrapper";
import {
  useFormControlInputA11y,
  useFormControlWrapper
} from "../FormControlWrapper/FormControlWrapperProvider";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  IconPickerInputProps,
  IconPickerInputPropsWithFormControl
} from "./IconPickerInput.types";

const classes = {
  FormControl: "gap-2 items-center",
  PreviewBox:
    "rounded-md border border-primary/60 border-2 h-10 w-10 items-center justify-center",
  EmptySelection: "bg-muted/60",
  PopoverContent: "bg-white p-0 w-[550px]",
  PopoverArrow: "fill-muted"
};

const IconPickerInputInput: React.FC<IconPickerInputProps> = (props) => {
  const {
    inputClassName,
    value: controlledValue,
    onChangeValue,
    className,
    children,
    tintColor,
    ...rest
  } = props;
  // The field label names the icon-only trigger through its id
  const { id } = useFormControlWrapper();
  const { "aria-describedby": ariaDescribedBy } = useFormControlInputA11y();
  const iconPickerDisclosure = useDisclosure();
  const [iconValue, setIconValue] = React.useState<IconValue | undefined>();

  const value = controlledValue ?? iconValue;
  const ink = tintColor ? getIconColorForBackground(tintColor) : undefined;

  const handleSelectIcon = (icon: IconValue) => {
    iconPickerDisclosure.close();
    setIconValue(icon);
    onChangeValue?.(icon);
  };

  return (
    <Flex
      {...rest}
      className={cn("w-full flex-row gap-2", inputClassName, className)}
    >
      <Popover
        open={iconPickerDisclosure.isOpen}
        onOpenChange={iconPickerDisclosure.setOpen}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            aria-describedby={ariaDescribedBy}
            variant={"outline"}
            className={"h-[46px] w-[46px] shrink-0 p-0"}
            style={
              tintColor ? { backgroundColor: tintColor, color: ink } : undefined
            }
          >
            <IconFromSet
              icon={(value as never) || { set: "lucide", icon: "Circle" }}
              size={6}
              color={ink}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          arrowProps={{ className: classes.PopoverArrow }}
          className={classes.PopoverContent}
          aria-label="Choose an icon"
        >
          <IconPicker onSelect={handleSelectIcon} />
        </PopoverContent>
      </Popover>
    </Flex>
  );
};

/**
 * An icon field: a square button showing the icon (a circle when empty) that opens
 * `IconPicker`. The value is `{ set, icon }`. Inside react-hook-form use
 * `HookFormIconPickerInput`.
 */
export const IconPickerInput = React.forwardRef<
  React.ComponentRef<typeof FormControlWrapper>,
  IconPickerInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <FormControlWrapper
      variant={"unstyled"}
      wrapperClassName={"py-0"}
      enableSlotsInputFocus
      ref={ref}
      {...wrapperProps}
    >
      <IconPickerInputInput {...inputProps} />
    </FormControlWrapper>
  );
});

IconPickerInput.displayName = "IconPickerInput";
